import { useMemo } from 'react';
import { WorkOrderItemData } from '../index';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { message, notification } from 'antd';
import { useDisclosure } from '@/hooks';
import { useWorkorderItemPermissions } from '../../WorkOrderItemPartForm/workorderItemPermissions';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import WorkOrderItemSpan from '../WorkOrderItemSpan';
import Authorities from '@/types/Authority';
import InputDecimalCell from '@/components/_common/InputDecimalCell';
import useWorkOrderItemUpdate from '../WorkOrderItemUtils/useWorkOrderItemUpdate';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';
import { isAppPortocar } from '@/index';
import formatterPercent from '@/utils/number/formatterPercent';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  item: WorkOrderItemData;
}

export default function WorkOrderItemDiscount(props: Props) {
  const [isInputEnabled, inputAction] = useDisclosure(false);
  const itemPermissions = useWorkorderItemPermissions();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { companyConfig },
  } = useWorkorderDetailContext();

  const isPart = props.item.type === WorkOrderItemType.Part;
  const isService = props.item.type === WorkOrderItemType.Service;
  const hasPolicy = !!complement?.policyNumber;

  const update = useWorkOrderItemUpdate(props.item);
  const partStockPrices = usePartStockPrices(
    props.item.id,
    isPart && isInputEnabled,
  );

  const stockPrices = useMemo(
    () =>
      partStockPrices.data?.result.find(
        (part) => part.company.id === workorder?.company.id,
      ),
    [workorder?.company.id, partStockPrices, partStockPrices.dataUpdatedAt],
  );

  const hasLowValuePermission = hasPermissions([
    Authorities.MOVIMENTATION_WORK_ORDER_ALLOW_TO_ENTER_ITEM_SALES_VALUE_BELOW_PROFIT_MARGIN,
  ]);

  const getMaxDiscountPart = () => {
    let max = undefined;
    const companyMaxDiscount = companyConfig?.maxPartDiscount ?? 0;

    if (!itemPermissions.giveDiscountOverLimit && stockPrices?.maxDiscount) {
      max = (stockPrices?.maxDiscount || 0) * 100;
    } else if (
      !itemPermissions.addDiscountGreaterDefinedByCompany &&
      typeof companyMaxDiscount === 'number'
    ) {
      max = companyMaxDiscount * 100;
    }

    return (max || 0) / 100;
  };

  const getMaxServiceDiscount = () => {
    return !itemPermissions.addDiscountGreaterDefinedByCompany &&
      !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OVER_LIMIT,
      ])
      ? (companyConfig?.maxServiceDiscount ?? 0) * 100
      : undefined;
  };

  const getMinDiscount = () => {
    if (
      isAppPortocar &&
      isService &&
      !workorder?.partnerDiscount &&
      workorder?.employeeDiscount
    )
      return 10;

    if (
      !isAppPortocar ||
      !isService ||
      !complement?.policyNumber ||
      !workorder?.partnerDiscount
    )
      return undefined;

    const isPortoPolicy =
      hasPolicy &&
      complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

    return isPortoPolicy ? 20 : 15;
  };

  const handleUpdateDiscount = async (discount: number) => {
    const maxDiscount = isPart ? getMaxDiscountPart() : getMaxServiceDiscount();
    const minDiscount = getMinDiscount();

    const sellPrice = stockPrices?.sellPrice || 0;
    const totalItem =
      props.item.unitPrice - (discount / 100) * props.item.unitPrice;

    if (!hasLowValuePermission && totalItem < sellPrice) {
      return notification.info({
        message: `Você não tem permissão para lançar valor total de venda do item abaixo da margem de lucro ${formatterPrice(
          sellPrice || 0,
        )}`,
      });
    }

    if (!!maxDiscount && discount > maxDiscount) {
      message.destroy();
      message.warn(
        `Desconto não pode ser maior que ${formatterPercent(
          maxDiscount / 100,
        )}`,
      );
    }

    if (!!minDiscount && discount < minDiscount) {
      message.destroy();
      return message.warn(
        `Desconto não pode ser menor que ${formatterPercent(
          minDiscount / 100,
        )}`,
      );
    }

    if (!itemPermissions.allowSellingBellowCostValue) {
      const subTotal =
        (props.item?.quantity || 0) * (props.item?.unitPrice || 0);

      const amount = subTotal - discount;

      if (amount < (props.item.unitCost || 0) * (props.item.quantity || 0)) {
        message.destroy();
        return message.warn(
          'O preço de venda deve ser maior que o custo unitário',
        );
      }
    }

    try {
      await update.mutateAsync({ key: 'discount', value: discount / 100 });
    } finally {
      inputAction.close();
    }
  };

  const permissionPart =
    !props.item.unitPrice ||
    (complement?.status !== WorkOrderComplementStatus.Open &&
      !itemPermissions.openedItemChangeDiscount) ||
    !itemPermissions.editDiscountValue;

  const permissionService =
    !props.item.unitPrice ||
    (!itemPermissions.addDiscountGreaterDefinedByCompany &&
      typeof companyConfig?.maxServiceDiscount !== 'number') ||
    (!props.item.isInternal && !itemPermissions.giveDiscountOutsourcedServices);

  if (isInputEnabled) {
    return (
      <InputDecimalCell
        defaultValue={props.item.discount * 100}
        min={0}
        max={100}
        loading={update.isLoading}
        onConfirmValue={(value) => handleUpdateDiscount(value)}
        onClose={inputAction.close}
      />
    );
  }

  return (
    <WorkOrderItemSpan
      item={props.item}
      onClick={inputAction.open}
      permission={isPart ? permissionPart : permissionService}
    >
      {(props.item.discount * 100) % 1 === 0
        ? String(props.item.discount * 100).replace('.', ',') + '%'
        : String((props.item.discount * 100).toFixed(2)).replace('.', ',') +
          '%'}
    </WorkOrderItemSpan>
  );
}
